/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, computed, IObservableArray, IReactionDisposer} from 'mobx';
import moment from 'moment';

/**************************************** PROJECT MODULES *****************************************/
import {BaseStore} from '../base-store';
import {Serializable, referenceProp} from '../../hocs/serialization';
import {RecordVisitBoxesUIModel} from './record-visit-boxes-ui-model';
import {PagesModel} from './pages-model';
import {LastRecordStateModel} from './last-record-state-model';

import {
    Record as JvlnsRecord,
    RecordInstance,
    PageInstance,
    DoseInstance,
    getRegionName,
    RegionAbbrevId,
    Gender,
    JavelinData,
    LocalDate,
    RelationshipType,
    Relationship,
    NotificationManager,
    DiseaseConceptInstance,
    NotificationInstance,
} from '@canimmunize/javelinscript';

import {RecordSubmenuTypes, Store, translate} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
type PagesModelInstance = ReturnType<(typeof PagesModel)['new']>;

/**
 * Get the type of a PagesModel instance prop / method
 */
type PagesModelPropType<PropName extends keyof PagesModelInstance> = PagesModelInstance[PropName];

/********************************************* EXPORT *********************************************/
/**
 * Store a JVLNS instance and the currently selected submenu item (left menu)
 */
@Serializable({
    isSingleton: false,
    underlyingData: {
        type: JvlnsRecord,
        idField: `recordId`,
        deleteEventName: `ev_DELETE_RECORD`,
    },
})
export class RecordModel extends BaseStore {
    private javelinData: JavelinData;

    /**
     * In the delete process, @Serializable runs disposers to remove event handlers
     */
    public disposers: IReactionDisposer[] = [];

    /*********** STORES ***********/
    /**
     * Store for UI of record's visit boxes
     */
    public recordVisitBoxesUIModel: RecordVisitBoxesUIModel;

    /**
     * Store for page-related data & methods (visit & dose handling and data)
     */
    private pagesModel: PagesModel;

    /**
     * Store for the previous settings values stored in the RecordModel
     * Purpose: to determine if full pages update required (do it if these values changed)
     */
    private lastRecordStateModel: LastRecordStateModel;

    /*********** CONCEPT IDs ***********/
    /**
     * Immutable influenza vaccine concept IDs
     */
    private influenzaVaccineConceptIds: string[] = [];

    /**
     * Immutable tetanus disease concept IDs
     */
    private tetanusDiseaseConceptIds: string[] = [];

    /*************** JVLNS/RECORD DATA PROPERTIES ***************/
    private _record: RecordInstance = null;

    /**
     * Actual record object returned from Javelinscript (jvln.getRecord())
     */
    @computed
    public get record(): RecordInstance {
        return this.rootStore.records.find(rec => rec.recordId === this._record.recordId);
    }

    /********** PROXIED TO PageModel **********/
    /**
     * Get Page objects from current record
     */
    @computed
    public get pages(): PagesModelPropType<'pages'> {
        return this.pagesModel.pages;
    }

    public get getPage(): PagesModelPropType<'getPage'> {
        return this.pagesModel.getPage;
    }

    public get getDosePageType(): PagesModelPropType<'getDosePageType'> {
        return this.pagesModel.getDosePageType;
    }

    public get currentPages(): PagesModelPropType<'currentPages'> {
        return this.pagesModel.currentPages;
    }

    /*************** UI DATA PROPERTIES ***************/
    /**
     *  Current item in submenu (`vaccinations`, or `settings`)
     */
    @observable
    public curSubmenuItem: RecordSubmenuTypes = `vaccinations`;

    /**
     * ID of currently active record
     */
    @referenceProp
    @observable
    public recordId: string = ``;

    /*************** VIRTUAL / COMPUTED PROPERTIES ***************/
    /**
     * Full name of region, in displayable string form (e.g. "Northwest Territories")
     */
    @computed
    public get regionName(): string {
        return getRegionName(translate, this.record.region.toUpperCase() as RegionAbbrevId);
    }

    /**
     * Get the user/person's full name (first name plus last name, with a space between)
     */
    @computed
    public get fullName(): string {
        return this.record.firstName
            ? `${this.record.firstName} ${this.record.lastName}`
            : this.record.firstName;
    }

    /**
     * Get the record's overall status by checking record notifications for
     * overdue and approaching vaccinations
     */
    @computed
    public get recordStatus(): string {
        const notifications = this.getRecordNotifications();

        const overdue = [];
        const approaching = [];

        notifications.forEach(notification => {
            if (notification.status === 0) return overdue.push(notification);
            if (notification.status === 1) return approaching.push(notification);
        });

        return overdue.length > 0 ? `overdue` : approaching.length > 0 ? `warning` : `okay`;
    }

    /**
     * Get the user/person's full name in lowercase snake_case, with all whitespace trimmed
     * Purpose: prepares it for use in a query parameter
     * @return {string} fullName cleaned for use in query param
     * @example Initial fullName value " Meeka Faulkner  " gives us "meeka_faulkner"
     */
    @computed
    public get fullNameQueryParam(): string {
        return this.fullName
            ? this.fullName
                  .toLowerCase()
                  .trim()
                  .snakeCase()
            : ``;
    }

    /******************** User data ********************/
    @computed
    public get genderStr(): string {
        return Gender.getString(this.record.gender);
    }

    @computed
    public get firstName(): string {
        return this.record.firstName;
    }
    public set firstName(firstName: string) {
        this.record.firstName = firstName;
    }

    @computed
    public get lastName(): string {
        return this.record.lastName;
    }
    public set lastName(lastName: string) {
        this.record.lastName = lastName;
    }

    @computed
    public get birthdate(): LocalDate {
        return this.record.birthdate;
    }
    public set birthdate(birthdate: LocalDate) {
        this.record.birthdate = birthdate;
    }

    @computed
    public get region(): RegionAbbrevId {
        return this.record.region as RegionAbbrevId;
    }
    public set region(region: RegionAbbrevId) {
        this.record.region = region as RegionAbbrevId;
    }

    @computed
    public get gender(): string {
        return Gender.getString(this.record.gender);
    }

    @computed
    public get relationship(): RelationshipType {
        return this.record.relationship as RelationshipType;
    }
    public set relationship(relationship: RelationshipType) {
        this.record.relationship = Relationship.getRelationshipNumFromRelationship(relationship);
    }

    /**
     * Get city ID from record
     */
    @computed
    public get city(): string {
        return this.record.cityId;
    }
    public set city(city: string) {
        this.record.cityId = city;
    }

    @computed
    public get doctor(): string {
        return this.record.doctor;
    }
    public set doctor(doctor: string) {
        this.record.doctor = doctor;
    }

    @computed
    public get notes(): string {
        return this.record.notes;
    }
    public set notes(notes: string) {
        this.record.notes = notes;
    }

    /*************** Notifications ***************/
    public getFluNotification = () =>
        NotificationManager.getFluNotification(
            this.javelinData.influenzaVaccineConceptIds,
            this.record,
            (this.pages as any) as PageInstance[]
        );

    /**
     * Note [2018/10/04]: slightly hacky workaround for Javelin's current lack
     * of awareness of whether influenza doses are being ignored or not
     *
     * E.g. if you set "Notifications: Off" (ignored) for the only upcoming
     * influenza dose, JVLNS should no longer return a notification saying a
     * flu shot is needed, but it currently does
     *
     * The below semi-hack handles a few common cases (all Inf doses ignored,
     * received, or no longer recommended) & uses the notification in all others
     */
    public needsFluShot = () => {
        // Get influenza doses
        const infDoses = this.pages
            .map(page => page.doses[0] && page.doses[0].abbreviation === `Inf` && page.doses)
            .rmFalsyVals<DoseInstance>()
            .flatMapDeep<any, DoseInstance>(infDose => infDose.array || infDose);

        // If no influenza doses, no flu shot is needed
        if (!infDoses) return false;

        // If all influenza doses are ignored, received, or past max age, no
        // flu shot is needed
        const isAnyFutureFluDose = infDoses.some(
            infDose =>
                !infDose.isIgnored(this.record) &&
                !infDose.isReceived &&
                !infDose.isMaxAgePast(this.record)
        );
        if (!isAnyFutureFluDose) return false;

        return this.getFluNotification();
    };

    public getTetanusNotification = () =>
        NotificationManager.getTetanusNotification(
            this.javelinData.tetanusDiseaseConceptIds,
            this.record,
            (this.pages as any) as PageInstance[]
        );

    public getRecordNotifications = (): NotificationInstance[] => {
        try {
            return NotificationManager.getRecordNotifications(
                this.record,
                (((this.pages as any) as PageInstance[]) && this.pages.rmFalsyVals()) || [],
                this.javelinData.influenzaVaccineConceptIds
            );
        } catch (err) {
            // Mostly for handling no overallDate, no date, no recommendedDate case, when
            // a dose has been "deleted" but the dose object hasn't yet been eliminated
            // [Likely cause: MobX triggering immediate UI updates on change to the dose object]
            return [];
        }
    };

    /**
     * Get notification data object containing all data required for a dose box
     *
     * @return {NotificationInstance} data object containing record, status
     *                                code (at status key), title string (at
     *                                title key), & data object (at obj key)
     */
    public getDoseBoxNotification = (dose: DoseInstance) => {
        try {
            return NotificationManager.getDoseBoxNotification(
                this.record,
                dose,
                dose.isReceived,
                dose.isIgnored(this.record),
                dose.date
            );
        } catch (err) {
            // Mostly for handling no overallDate, no date, no recommendedDate case, when
            // a dose has been "deleted" but the dose object hasn't yet been eliminated
            // [Likely cause: MobX triggering immediate UI updates on change to the dose object]
            return {record: null, status: null, title: ``};
        }
    };

    public getDiseaseNotification = (concept: DiseaseConceptInstance) =>
        NotificationManager.getDiseaseNotification(concept, this.record, (this
            .pages as any) as PageInstance[]);

    /**
     * Get total # of received vs. expected vaccinations for the given disease concept (in a Pair)
     * @return {first: Int, second: Int} 1st: # of received vaccinations for disease; 2nd: expected
     */
    public getDiseaseTotals = (concept: DiseaseConceptInstance): {first: number; second: number} =>
        NotificationManager.getDiseaseTotals(concept, this.record, (this
            .pages as any) as PageInstance[]);

    /*************** INITIALIZATION ***************/
    constructor(public rootStore: Store, record: RecordInstance) {
        super();
        // Added for convenience
        this.javelinData = rootStore.javelinData;

        // Set initial record & recordId for referencing
        this._record = record;
        this.recordId = record.recordId;

        // Store initial record values to later determine if full pages update required
        this.lastRecordStateModel = LastRecordStateModel.new(record);

        // Create store for wrapping & handling record's Page object collection
        this.pagesModel = PagesModel.new(rootStore, this, this.lastRecordStateModel);

        // TODO move RecordVisitBoxesUIModel out of RecordModel - this is terrible separation of concerns
        // Create store for handling display of record data in UI
        this.recordVisitBoxesUIModel = RecordVisitBoxesUIModel.new(this, this.pagesModel);
    }

    /*************** ACTIONS (STANDALONE) ***************/
    public setRecord = action((record: RecordInstance) => this.setData(`record`, record));

    /**
     * Set vaccinations or settings item under record
     * vaccinations = My Records content area for the record
     * settings = Record Settings for the record
     *
     * Includes setting current active tab in the record if vaccinations is selected
     */
    public setCurrentSubmenuItem = action((curSubmenuItem: RecordSubmenuTypes) => {
        // Handle case where 'vaccinations' is selected
        if (curSubmenuItem === `vaccinations`) {
            // Put calc before 'if' below to ensure tab switch happens right
            // after record switch
            const adultCutoff = LocalDate.new(moment().subtract(16, `years`));
            const isAdult = this.birthdate.isBefore(adultCutoff);
            this.setData(`curSubmenuItem`, `vaccinations`, true, `RECORDS`);
            if (!this.recordVisitBoxesUIModel.hasTabSwitched) {
                if (isAdult) this.recordVisitBoxesUIModel.setActiveVisitTypeTab(`adult`);
            }
        }

        // Handle case where 'settings' is selected
        else {
            this.setData(`curSubmenuItem`, `settings`, true, `RECORDS`);
        }
    });
}
